import debug from "debug";
import Experience from "../../Experience/Experience";
const log = debug("app:ShapeLoader ");

/**
 * A class responsible for loading and managing shape data, including fetching part data and transforming it for use.
 * @category Components
 * @subcategory ShapeManager
 */
export default class ShapeLoader {
  constructor() {
    this.experience = new Experience();
    this.eventHub = this.experience.eventHub;

  }


  cleanup() {
    console.log("ShapeLoader cleanup started...");

    // 1. Remove event listeners from eventHub
    if (this.fetchDataResponseHandler) {
      this.eventHub.off("responseEvent", this.fetchDataResponseHandler);
      this.fetchDataResponseHandler = null;
    }
  
    // 2. Clear object references
    this.experience = null;
    this.eventHub = null;
  
    console.log("ShapeLoader cleanup completed.");
  }
  /**
   * Fetches data for a given part number, including dimensions, textures, and additional properties.
   * @param {string} partNumber - The unique identifier of the part to fetch data for.
   * @returns {Promise<Object>} A promise that resolves to the part's data, including dimensions, textures, and other properties.
   * @throws {Error} Throws an error if the fetch operation fails.
   */
  async fetchDataForPartNumber(partNumber) {
    const dataVaultResponse = await this.fetchFromDataVault(
      "requestEvent",
      partNumber,
      "responseEvent"
    );
    const data = await dataVaultResponse.json();

    const urlForFfid = `${ffUrl}/${data.ffId}`;
    const response = await fetch(urlForFfid, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const ffData = await response.json();

    return {
      shapeWidth: ffData.w * 100,
      shapeHeight: ffData.h * 100,
      textured: `${assetURL}${ffData.images[0].imageURL}`,
      hybridTexture: `${assetURL}${data.images[0].imageURL}`,
      purpleTexture: `${assetURL}${ffData.images[1].imageURL}`,
      layer: data.layer,
      mode: "xm-part",
      price: data.price,
      category: "category", // Add category if needed
      ports: ffData.ports,
    };
  }

  /**
   * Fetches data from a DataVault by emitting and listening to events.
   * @param {string} requestEvent - The event to emit for requesting data.
   * @param {string} id - The identifier of the data to fetch.
   * @param {string} responseEvent - The event to listen to for the response.
   * @returns {Promise<Response>} A promise that resolves to a response-like object containing the requested data.
   */
  fetchFromDataVault(requestEvent, id, responseEvent) {
    return new Promise((resolve, reject) => {
      const responseHandler = (data) => {
        log(`Received data for ${responseEvent}:`, data);
        resolve({
          ok: true,
          headers: new Headers(),
          json: () => Promise.resolve({ data }), // Mimic the original response structure
        });
        this.eventHub.off(responseEvent, responseHandler); // Clean up the event listener
      };

      this.eventHub.on(responseEvent, responseHandler);
      log(`Emitting ${requestEvent} with id:`, id);
      this.eventHub.emit(requestEvent, id);
    });
  }

  /**
   * Loads shape data from XMD (eXtended Module Description) format.
   * @param {Object} xmdData - The XMD data object containing figures and their attributes.
   * @param {Array<Object>} xmdData.figures - The list of figures to process.
   * @param {Object} xmdData.figures[].partData - The part data for each figure.
   * @param {string} xmdData.figures[].partData.fullpartnumber - The full part number of the figure.
   * @param {number} xmdData.figures[].x - The x-coordinate of the figure.
   * @param {number} xmdData.figures[].y - The y-coordinate of the figure.
   * @param {number} xmdData.figures[].z - The z-coordinate of the figure.
   * @returns {Promise<Array<Object>>} A promise that resolves to an array of shape data objects.
   */
  async loadFromXmd(xmdData) {
    const promises = xmdData.figures.map(async (figure) => {
      const partParams = await this.fetchDataForPartNumber(
        figure.partData.fullpartnumber
      );
      return {
        ...partParams,
        x: figure.x,
        y: figure.y,
        z: figure.z,
        partNumber: figure.partData.fullpartnumber,
        meshes: [],
        originalMaterials: [],
        Objects: [],
        objectsAndPlane: [],
        connectionPoints: [],
        objectsToMove: [],
        plane: null,
        meshAndTexture: [],
        meshAndPurTexture: [],
        ctlMeshes: [],
      };
    });

    return Promise.all(promises);
  }
}
